// Shared display formatting for money, dates and counts. Money is always
// integer cents in; everything is formatted for en-CA so digit grouping
// doesn't follow the viewer's browser locale.

const LOCALE = "en-CA";

// $12.50, $1,250 — cents shown only when there are some (or always, with
// `cents: true`, for receipts and ledgers).
export function formatMoney(cents, { cents: showCents = false } = {}) {
  const amount = (Number(cents) || 0) / 100;
  const digits = showCents || !Number.isInteger(amount) ? 2 : 0;
  return `$${amount.toLocaleString(LOCALE, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
}

export function formatNumber(n) {
  return (Number(n) || 0).toLocaleString(LOCALE);
}

// 1 student, 2 students
export function pluralize(count, word, plural = `${word}s`) {
  return `${formatNumber(count)} ${count === 1 ? word : plural}`;
}

// A whole percentage of a goal, capped at 100. `floor: true` rounds
// down, so a bar never reads 100% before the goal is met (the TV display).
export function percentOf(value, goal, { floor = false } = {}) {
  if (!goal) return 0;
  const pct = ((Number(value) || 0) / goal) * 100;
  return Math.min(100, floor ? Math.floor(pct) : Math.round(pct));
}

// "Oct 19, 2026" (or "Oct 19" with year: false). Accepts an ISO date
// ("2026-10-19", read as that calendar day) or timestamp.
export function formatDate(value, { year = true, weekday = false } = {}) {
  if (!value) return "";
  const dateOnly = typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
  const date = dateOnly ? new Date(`${value}T12:00:00Z`) : new Date(value);
  return date.toLocaleDateString(LOCALE, {
    month: "short",
    day: "numeric",
    ...(year && { year: "numeric" }),
    ...(weekday && { weekday: "short" }),
    ...(dateOnly && { timeZone: "UTC" }),
  });
}

// "Oct 19, 2026, 3:05 p.m."
export function formatDateTime(value) {
  if (!value) return "";
  return new Date(value).toLocaleString(LOCALE, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

// "Oct 19 – 30, 2026", "Oct 19 – Nov 2, 2026", "Dec 28, 2026 – Jan 8, 2027"
export function formatDateRange(start, end) {
  if (!start || !end) return formatDate(start || end);
  const a = new Date(start);
  const b = new Date(end);
  if (a.getFullYear() !== b.getFullYear()) return `${formatDate(start)} – ${formatDate(end)}`;
  const sameMonth = a.getMonth() === b.getMonth();
  const endLabel = sameMonth ? b.toLocaleDateString(LOCALE, { day: "numeric" }) : formatDate(end, { year: false });
  return `${formatDate(start, { year: false })} – ${endLabel}, ${b.getFullYear()}`;
}
